import DateFnsUtils from "@date-io/date-fns";
import { createMuiTheme } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import router, { useRouter } from "next/router";
import React, { useEffect, useState } from "react";
import { StaticRouter } from "react-router-dom";
import aphTheme from "../aphTheme";
import { AuthProvider } from "../components/AuthProvider";
import { DiagnosticsCartProviderGlobal } from "components/Tests/DiagnosticsCartProviderGlobal";
import Consent from "components/Consent";
import { OTPProvider } from "components/OTPProvider";
import { DiagnosticsCartProvider } from "components/Tests/DiagnosticsCartProvider";
import "../components/icomoon/style.css";
import dynamic from "next/dynamic";
import "../../styles/common.scss";
import "@aph/ui-lib/dist/style.css";
import { ClevertapBannerProvider } from "../components/ClevertapBannerProvider";
import ProgressBar from "../components/ProgressBar";
import { init as initApm } from "@elastic/apm-rum";
import Header from "../components/Header/Header";
import { IS_APP_USER } from "helpers/constants";
import Footer from "../components/Footer/Footer";
import { dataLayerTracking } from "gtmTracking";
import { isServer, getSectionNameOnRoute } from "helpers/commonHelpers";
import "@aph/ui-lib/dist/style.css";
import { updateUserProfileUTMSource } from "../clevertapTracking";

const theme = createMuiTheme({ ...aphTheme });

const DynamicPageFonts = dynamic(
  () =>
    import("../fonts/fonts.module.css").then((defaultComponent) => () => null),
  { ssr: true }
);

const DynamicSubscriptionMembershipProvider = dynamic(
  () =>
    import("../components/SubscriptionMembershipProvider").then(
      (defaultComponent) => defaultComponent.SubscriptionMembershipProvider
    ),
  { ssr: true }
);

const App = ({ Component, pageProps }) => {
  const isDiagnosticsCartProviderRequired = pageProps.isDiagCartProRequired;
  const router = useRouter();
  const isProd = process.env.NEXT_PUBLIC_NODE_ENV === "production";
  const [isUserFromApp, setUserFromApp] = useState(false);
  const pageLOB = pageProps.pageLOB;
  const pagetype = pageProps.pagetype;
  const sectionName = pageProps.sectionName || getSectionNameOnRoute(router);
  const utm_source = router.query?.utm_source || "";

  useEffect(async () => {
    const dataLayerEvents = {
      event: "custom_pageview",
      page_name: (await document.getElementsByTagName("title")[0]?.innerText)
        ?.length
        ? document.getElementsByTagName("title")[0]?.innerText
        : isServer().href?.includes("www")
        ? isServer().href?.split("https://www.")[1]
        : isServer().href?.includes("https")
        ? isServer().href?.split("https://")[1]
        : isServer().href?.split("http://")[1],
      page_url: isServer()?.href,
      lob: pageLOB,
      section: sectionName,
      page_type: pagetype,
      client_id: localStorage.getItem("currentUser") || "",
    };
    dataLayerTracking(dataLayerEvents);
  });

  useEffect(() => {
    if (utm_source.length) {
      updateUserProfileUTMSource(utm_source);
    }
  }, [utm_source]);

  useEffect(() => {
    const handleRouteChange = (url) => {
      if (window && window.gtag) {
        window.gtag("config", process.env.NEXT_PUBLIC_GA_TRACKING_ID, {
          page_path: url,
          cookie_flags: "max-age=7200;secure;samesite=none",
        });
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", function () {
        navigator.serviceWorker.register("/service-worker.js").then(
          function (registration) {
            console.log(
              "Service Worker registration successful with scope: ",
              registration.scope
            );
          },
          function (err) {
            console.log("Service Worker registration failed: ", err);
          }
        );
      });
    }

    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      setUserFromApp(
        urlParams.get("header") === "false" ||
          urlParams.get("user_from_device") === IS_APP_USER.APP_USER
          ? true
          : false
      );
    }
  }, []);

  useEffect(() => {
    // Remove the server-side injected CSS.
    // this will remove white screen of the page
    const jssStyles = document.querySelector("#jss-server-side");
    if (jssStyles) {
      jssStyles.parentElement.removeChild(jssStyles);
    }
    //real use monitoring Elastic RUM
    isProd &&
      initApm({
        serviceName: "Apollo-Web-SSR-RUM",
        serviceVersion: "1.0",
        environment: process.env.NEXT_PUBLIC_NODE_ENV,
        transactionSampleRate: parseFloat(
          process.env.NEXT_PUBLIC_APM_SAMPLE_RATE
        ),
        serverUrl: process.env.NEXT_PUBLIC_APM_SERVER_URL,
      });
  }, []);

  const pageLayout = () => {
    return (
      <>
        {!(isUserFromApp || pageProps?.hideCommonHeader) && (
          <Header
            backArrowVisible={pageProps?.backArrowVisible}
            backLocation={pageProps?.backLocation}
            showCompanyLogo={pageProps?.showCompanyLogo}
            isWebView={pageProps?.isWebView}
            isRouterFromPayCare={pageProps?.isRouterFromPayCare}
            isSymptomChecker={pageProps?.isSymptomChecker}
            mWebHeaderHide={pageProps?.mWebHeaderHide}
            sectionName={sectionName}
            headerSize={pageProps?.headerSize}
          />
        )}
        <Component {...pageProps} />
        {pageProps.footerLinks && !isUserFromApp && (
          <Footer
            footerData={pageProps?.footerLinks}
            sectionName={sectionName}
            footerSize={pageProps?.footerSize}
          />
        )}
      </>
    );
  };

  return (
    <>
      <StaticRouter>
        <AuthProvider sectionName={sectionName} lob={pageLOB}>
          <OTPProvider>
            <ThemeProvider theme={theme}>
              <DynamicSubscriptionMembershipProvider>
                <DiagnosticsCartProviderGlobal>
                  <ClevertapBannerProvider>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <ProgressBar />
                      <CssBaseline />
                      <Consent />
                      {isDiagnosticsCartProviderRequired && (
                        <DiagnosticsCartProvider>
                          {pageLayout()}
                        </DiagnosticsCartProvider>
                      )}
                      {!isDiagnosticsCartProviderRequired && pageLayout()}
                    </MuiPickersUtilsProvider>
                  </ClevertapBannerProvider>
                </DiagnosticsCartProviderGlobal>
              </DynamicSubscriptionMembershipProvider>
            </ThemeProvider>
          </OTPProvider>
        </AuthProvider>
        <DynamicPageFonts />
      </StaticRouter>

      <style jsx global>{`
        a {
          text-decoration: none;
          color: inherit;
          outline: 0;
        }
        html,
        body {
          height: 100%;
          width: 100%;
          padding: 0;
          font-display: fallback;
        }
        *,
        *:after,
        *:before {
          box-sizing: border-box;
        }
        ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }

        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        ::-webkit-scrollbar-thumb {
          background: #ccc;
        }
        .clevertapCloseBtn {
          position: fixed;
          border: none;
          background: none;
          outline: none;
          cursor: pointer;
          z-index: 1000;
          display: none;
        }
        @media screen and (max-width: 767px) {
          .clevertapCloseBtn {
            right: 0;
            display: inline-block;
            width: 20px;
            height: 20px;
            top: 0;
          }
        }
        @page {
          margin: 10mm 5mm;
        }
        @media print {
          thead {
            display: table-header-group;
          }
          tfoot {
            display: table-footer-group;
          }

          body {
            margin: 0;
          }
        }
      `}</style>
    </>
  );
};

export default App;
